/**
 * Helpers de ruta compartidos entre `pages/index.jsx` y los manifests de
 * `src/features/<modulo>/routes.jsx` (Fase 0 / PR 0.5a).
 *
 * Viven aquí y no en `index.jsx` por una razón concreta: los manifests los
 * necesitan, y si los importaran de `index.jsx` se crearía el ciclo
 * `index → features → index` — exactamente lo que el trinquete de fronteras
 * existe para impedir. Un módulo hoja del que todos pueden tirar no lo crea.
 *
 * `AriaHome` se declara aquí, y no en `features/ai/routes.jsx`, porque lo montan
 * dos sitios: `/aria-home` (de ai) y `HomeRoute`, que es el landing del shell.
 * Alojarlo en ai obligaría al shell a depender de ai, y la regla
 * `nadie-depende-de-ai` es el prerrequisito de la extracción de ARIA en Fase 4.
 * Así la flecha va en el sentido bueno: ai importa de aquí.
 */
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import { RouteFallback } from '@/components/common/RouteFallback';
import { useCanSeePortalAsesor, usePortalBasePath, usePortalGateReady, useShouldLandOnPortal } from '@/components/hooks/useCanSeePortalAsesor';

export const AriaHome = lazyWithRetry(() => import('./AriaHome'));

// Canonical page/suspense fallback. Name kept so all Suspense call sites below
// stay unchanged; internals delegate to the shared RouteFallback (token-based,
// dark-mode aware, density-aware).
export function PageLoader() {
  return <RouteFallback full label="Cargando módulo..." />;
}

// Preserves query string when redirecting legacy capitalized URLs.
export function RedirectKeepSearch({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

// HomeRoute — landing gate. `/` y `/home` muestran el ARIA Home (chat-first).
export function HomeRoute() {
  // El home del producto es el de ARIA, en todos los casos. Antes se elegía
  // entre dos —el flag `aria.home`, el permiso `ai.use` y la preferencia
  // `home_layout` podían devolver el dashboard clásico— y eso dejaba a distintos
  // usuarios con distintas páginas de entrada según una combinación que nadie
  // veía. El dashboard clásico ya no existe: se retiró del repo.
  //
  // ⚠️ Lo que se pierde con esto: la paridad con el `PermissionGuard ai.use` de
  // `/asistente-agentic`. Un rol SIN `ai.use` aterriza ahora en el panel de
  // ARIA, cuyas peticiones responderán 403. Decisión explícita; si aparece un
  // rol así en producción, la salida es darle `ai.use` o devolverle aquí un
  // destino propio, no resucitar el dashboard.
  return <AriaHome />;
}

// FirmHomeGate — home de `/`. El landing solo redirige al portal cuando el
// usuario realmente lo necesita como home: un asesor de despacho real
// (firmMember) SIEMPRE, o una cuenta que gestiona >1 empresa (modo group →
// "Mis empresas"). Una cuenta con UNA sola empresa (aun con asesor.view /
// accountant_external) cae en `HomeRoute` — su página de entrada es el Home.
// Ver `shouldLandOnPortal`. El resto de usuarios (empresas normales) también
// caen en `HomeRoute`. `/home` NO pasa por aquí: es el home de la empresa activa.
export function FirmHomeGate() {
  const ready = usePortalGateReady();
  const shouldLandOnPortal = useShouldLandOnPortal();
  const portalBasePath = usePortalBasePath();
  // Espera a la sesión Y al count de empresas. Esperar solo a la sesión dejaba
  // fuera el modo 'group': `firmMember` viene en `/me`, pero "más de una
  // empresa" sale de un segundo fetch, y mientras vuela el count vale 0 — que
  // es exactamente la cuenta mono-empresa, la que SÍ va al home. Resultado:
  // ARIA se montaba, hacía sus peticiones y se sustituía por "Mis empresas".
  // Ver `usePortalGateReady`; fijado en `__tests__/FirmHomeGate.test.jsx`.
  //
  // `PageLoader` y no `null`: esta es la primera pantalla tras el login, y un
  // blanco mientras responde `/api/auth/companies` (cold start de la Lambda) se
  // lee como que la app no ha arrancado.
  if (!ready) return <PageLoader />;
  if (shouldLandOnPortal) return <Navigate to={portalBasePath} replace />;
  return <HomeRoute />;
}

// FirmRouteGate — gate de acceso de `/portal-asesor/*`. Antes vivía dentro de
// `AsesorShell`; al pasar el shell a chrome despachado por `Layout`, el gate se
// mantiene aquí a nivel de ruta. El chrome (cabecera + tabbar) lo monta el
// dispatcher de `Layout` (`isFirmEcosystem`); este wrapper solo gatea + Outlet.
export function FirmRouteGate() {
  const ready = usePortalGateReady();
  const canSeePortal = useCanSeePortalAsesor();
  // Mismo motivo que en `FirmHomeGate`, en espejo: entrar directo a
  // `/mis-empresas` con el count sin resolver daba `canSeePortal` falso y
  // rebotaba a `/` antes de que llegara la respuesta.
  if (!ready) return <PageLoader />;
  if (!canSeePortal) return <Navigate to="/" replace />;
  return <Outlet />;
}

// Redirige una ruta CCAA vieja a la lente fusionada preservando la query
// (modelo/ejercicio de los deep-links de Memoria/XBRL/CuentasAnuales).
export function CcaaTabRedirect({ tab }) {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  params.set('tab', tab);
  return <Navigate to={`/accounting/cuentas-anuales?${params.toString()}`} replace />;
}

// `/accounting/import` → `/datos`. Preserva la query (`?slice=expenses` y demás
// deep-links repartidos por Gastos, Facturas y Nóminas siguen funcionando).
export function MigrationRedirect() {
  const { search } = useLocation();
  return <Navigate to={`/datos${search}`} replace />;
}
